import type { User } from "@/models";
import { formatDistanceToNow } from "date-fns";

export * from "./browse";
export * from "./contact";
export * from "./covers";
export * from "./messages";

export function toggleInArray<T>(arr: T[], value: T): T[] {
    return arr.includes(value)
        ? arr.filter((v) => v !== value)
        : [...arr, value];
}

export function parseHtmlString(htmlString: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");
    return doc.body.textContent || "";
}

export function usernameIsEmail(user: User | null): boolean {
    if (!user) return false;
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(user.username);
}

export function formatMaybeDistance(value?: string | null) {
    if (!value) {
        return null;
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return null;
    }
    return formatDistanceToNow(date, { addSuffix: true });
}

/**
 * Format bytes to human-readable file size (e.g., "245.32KB")
 */
export function formatFileSize(bytes: number) {
    if (bytes === 0) return "0B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100}${sizes[i]}`;
}

/**
 * Extract the file name from a data URL
 */
export function urlToFilename(url: string) {
    const dataUrlRegex = /^data:(.*?);base64,(.*)$/;
    const UrlRegex = /^http[s]?:\/\/.+\/(.+)$/;
    const dataUrlMatch = url.match(dataUrlRegex);
    if (dataUrlMatch) {
        return `file.${dataUrlMatch[1].split("/")[1]}`;
    }
    const urlMatch = url.match(UrlRegex);
    if (urlMatch) {
        const parsedUrl = new URL(url);
        const pathname = parsedUrl.pathname;
        return pathname.substring(pathname.lastIndexOf("/") + 1);
    }
    return "file";
}

/**
 * Download a data URL as a file
 */
export function downloadDataUrl(dataUrl: string, filename: string): void {
    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = filename;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}
